import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import { CopyText } from "../elements/CopyText"
import { Dropdown, Modal } from "../../submodules/rapdv/client/elements/Bootstrap"
import { SocketClient } from "../socket/SocketClient"
import { SocketEvents } from "../../server/socket/SocketEvents"
import { FoodOrderDeliveryType } from "../../server/Types"
import { PushNotifications } from "../elements/PushNotifications"
import { SoundAlarm } from "../elements/SoundAlarm"

export class PageOrders implements ClientPage {

  private static readonly CSS_ACTIVE = "active"
  private static readonly CSS_HIDDEN = "d-none"
  private static readonly CSS_ORDER_SELECTED = "order-selected"
  private static readonly PWA_BAR_DISMISS_KEY = "orders-pwa-bar-dismissed"
  private static readonly PWA_BAR_DISMISS_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

  private pageEl: HTMLElement
  private pwaDeferredPrompt: any = null
  private baseUrl: string
  private rejectModal: any = null
  private pendingRejectOrderId: string | null = null
  private acceptModal: any = null
  private pendingAcceptOrderId: string | null = null
  private refundModal: any = null
  private pendingRefundOrderId: string | null = null
  private orderUpdatedHandler: ((...args: any[]) => void) | null = null
  private orderNewHandler: ((...args: any[]) => void) | null = null
  private orderPendingHandler: ((...args: any[]) => void) | null = null
  private connectionChangeHandler: ((connected: boolean) => void) | null = null

  getPageId = (): string => "page-orders"

  execute = (): void => {
    this.pageEl = document.querySelector("[data-orders-page]")
    if (!this.pageEl) return

    this.baseUrl = `/${this.pageEl.dataset.locationCode}/orders/${this.pageEl.dataset.restaurantKey}`

    this.setupWelcomeModal()
    this.setupUpgradeModal()
    this.setupDeliveryMethodsModal()
    this.setupRejectModal()
    this.setupAcceptModal()
    this.setupRefundModal()
    this.setupOrderCards()
    this.setupBackButton()
    this.setupActionButtons()
    this.restoreUrlState()
    this.setupSocketListeners()
    this.setupConnectionStatus()
    this.setupPwaBar()
    this.setupSoundAlarm()
    void PushNotifications.setup()
  }

  private setupSoundAlarm = (): void => {
    this.pageEl.addEventListener("click", (event) => {
      const toggle = (event.target as HTMLElement)?.closest("[data-sound-alarm-toggle]") as HTMLElement | null
      if (!toggle) return
      SoundAlarm.setEnabled(!SoundAlarm.isEnabled())
      SoundAlarm.syncButton(toggle)
    })
  }

  private syncSoundAlarmButton = (): void => {
    const toggle = this.pageEl.querySelector("[data-sound-alarm-toggle]") as HTMLElement | null
    if (toggle) SoundAlarm.syncButton(toggle)
  }

  private setupPwaBar = (): void => {
    const bar = this.pageEl.querySelector("[data-orders-pwa-bar]") as HTMLElement | null
    if (!bar) return

    // Hide in the installed PWA (iOS standalone isn't covered by the CSS display-mode media query).
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true
    if (isStandalone) {
      bar.classList.add(PageOrders.CSS_HIDDEN)
      return
    }

    const dismissedAt = localStorage.getItem(PageOrders.PWA_BAR_DISMISS_KEY)
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < PageOrders.PWA_BAR_DISMISS_MS) {
      bar.classList.add(PageOrders.CSS_HIDDEN)
      return
    }

    const installBtn = bar.querySelector("[data-pwa-install-btn]") as HTMLElement | null
    // The whole bar stays hidden until the app is actually installable, so the message never
    // shows without a working Install button.
    window.addEventListener("beforeinstallprompt", (event: Event) => {
      event.preventDefault()
      this.pwaDeferredPrompt = event
      bar.classList.remove(PageOrders.CSS_HIDDEN)
      installBtn?.classList.remove(PageOrders.CSS_HIDDEN)
    })

    installBtn?.addEventListener("click", async () => {
      if (!this.pwaDeferredPrompt) return
      this.pwaDeferredPrompt.prompt()
      await this.pwaDeferredPrompt.userChoice
      this.pwaDeferredPrompt = null
      bar.classList.add(PageOrders.CSS_HIDDEN)
    })

    bar.querySelector("[data-pwa-bar-close]")?.addEventListener("click", () => {
      bar.classList.add(PageOrders.CSS_HIDDEN)
      localStorage.setItem(PageOrders.PWA_BAR_DISMISS_KEY, String(Date.now()))
    })
  }

  private restoreUrlState = (): void => {
    const params = new URLSearchParams(window.location.search)
    const orderId = params.get("orderId")

    if (orderId) {
      const detailBody = this.pageEl.querySelector("[data-orders-detail-body]")
      const preRendered = detailBody?.querySelector("[data-order-detail]")
      if (preRendered) {
        this.highlightCard(orderId)
        this.pageEl.classList.add(PageOrders.CSS_ORDER_SELECTED)
        this.syncSoundAlarmButton()
      } else {
        this.selectOrder(orderId)
      }
    }
  }

  private updateUrlParam = (key: string, value: string | null): void => {
    const params = new URLSearchParams(window.location.search)
    if (value === null) {
      params.delete(key)
    } else {
      params.set(key, value)
    }
    const search = params.toString()
    history.replaceState(null, "", search ? `${window.location.pathname}?${search}` : window.location.pathname)
  }

  private setupWelcomeModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-welcome-modal]") as HTMLElement
    if (!modalEl) return

    const modal = new Modal(modalEl)
    modal.show()

    const copyBtn = modalEl.querySelector("[data-copy-url]") as HTMLElement
    copyBtn?.addEventListener("click", () => {
      CopyText.toClipboard(copyBtn.dataset.copyUrl)
    })
  }

  private setupUpgradeModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-upgrade-modal]") as HTMLElement
    if (!modalEl) return
    const modal = new Modal(modalEl)
    modal.show()
  }

  private setupDeliveryMethodsModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-delivery-methods-modal]") as HTMLElement
    if (!modalEl) return
    const modal = new Modal(modalEl)
    modal.show()
  }

  private setupRejectModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-reject-modal]") as HTMLElement
    if (!modalEl) return

    this.rejectModal = new Modal(modalEl)

    const otherField = modalEl.querySelector("[data-reject-other-field]") as HTMLElement
    const otherInput = modalEl.querySelector("[name='rejectReasonOther']") as HTMLInputElement

    const radios = modalEl.querySelectorAll("[data-reject-reason-radio]")
    for (const radio of radios) {
      radio.addEventListener("change", () => {
        const isOther = (radio as HTMLInputElement).dataset.isOther === "true"
        otherField?.classList.toggle(PageOrders.CSS_HIDDEN, !isOther)
        if (!isOther) {
          otherInput?.classList.remove("is-invalid")
        }
      })
    }

    otherInput?.addEventListener("input", () => otherInput.classList.remove("is-invalid"))

    const confirmBtn = modalEl.querySelector("[data-reject-confirm]") as HTMLElement
    confirmBtn?.addEventListener("click", () => {
      const selectedRadio = modalEl.querySelector("input[name='rejectReason']:checked") as HTMLInputElement
      const isOther = selectedRadio?.dataset.isOther === "true"

      if (isOther) {
        if (!otherInput?.value.trim()) {
          otherInput?.classList.add("is-invalid")
          return
        }
      }

      const reason = isOther ? otherInput.value.trim() : selectedRadio?.value
      const orderId = this.pendingRejectOrderId
      this.rejectModal.hide()
      this.pendingRejectOrderId = null
      if (!orderId) return
      this.rejectOrder(orderId, reason)
    })

    this.submitModalOnEnter(modalEl, confirmBtn)
  }

  private submitModalOnEnter = (modalEl: HTMLElement, confirmBtn: HTMLElement | null): void => {
    modalEl.addEventListener("keydown", (event) => {
      const keyboardEvent = event as KeyboardEvent
      if (keyboardEvent.key !== "Enter") return

      const target = keyboardEvent.target as HTMLElement
      if (target.tagName === "BUTTON" || target.tagName === "A" || target.tagName === "TEXTAREA") return

      keyboardEvent.preventDefault()
      confirmBtn?.click()
    })
  }

  private updateRejectReasonVisibility = (isDelivery: boolean): void => {
    const modalEl = this.pageEl?.querySelector("[data-reject-modal]") as HTMLElement
    if (!modalEl) return

    const deliveryOnlyRows = modalEl.querySelectorAll("[data-reject-reason-row][data-delivery-only='true']")
    for (const row of deliveryOnlyRows) {
      row.classList.toggle(PageOrders.CSS_HIDDEN, !isDelivery)
      const radio = row.querySelector("input[name='rejectReason']") as HTMLInputElement
      if (radio && !isDelivery && radio.checked) {
        radio.checked = false
        const firstRadio = modalEl.querySelector("input[name='rejectReason']") as HTMLInputElement
        if (firstRadio) firstRadio.checked = true
      }
    }
  }

  private setupAcceptModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-accept-modal]") as HTMLElement
    if (!modalEl) return

    this.acceptModal = new Modal(modalEl)

    const waitingInput = modalEl.querySelector("[name='waitingTimeMin']") as HTMLInputElement

    const presets = modalEl.querySelectorAll("[data-waiting-time-preset]")
    for (const btn of presets) {
      btn.addEventListener("click", () => {
        const waitingTimeMin = parseInt((btn as HTMLElement).dataset.waitingTimePreset, 10)
        const orderId = this.pendingAcceptOrderId
        this.acceptModal.hide()
        this.pendingAcceptOrderId = null
        if (!orderId) return
        this.acceptOrder(orderId, waitingTimeMin)
      })
    }

    waitingInput?.addEventListener("input", () => waitingInput.classList.remove("is-invalid"))

    const confirmBtn = modalEl.querySelector("[data-accept-confirm]") as HTMLElement
    confirmBtn?.addEventListener("click", () => {
      const waitingTimeMin = parseInt(waitingInput?.value, 10)
      if (!waitingInput?.value.trim() || isNaN(waitingTimeMin) || waitingTimeMin < 1) {
        waitingInput?.classList.add("is-invalid")
        return
      }

      const orderId = this.pendingAcceptOrderId
      this.acceptModal.hide()
      this.pendingAcceptOrderId = null
      if (!orderId) return
      this.acceptOrder(orderId, waitingTimeMin)
    })

    this.submitModalOnEnter(modalEl, confirmBtn)
  }

  private setupRefundModal = (): void => {
    const modalEl = this.pageEl.querySelector("[data-refund-modal]") as HTMLElement
    if (!modalEl) return

    this.refundModal = new Modal(modalEl)

    const confirmBtn = modalEl.querySelector("[data-refund-confirm]") as HTMLElement
    confirmBtn?.addEventListener("click", () => {
      const orderId = this.pendingRefundOrderId
      this.refundModal.hide()
      this.pendingRefundOrderId = null
      if (!orderId) return
      this.refundOrder(orderId)
    })

    this.submitModalOnEnter(modalEl, confirmBtn)
  }

  private setupDropdowns = (): void => {
    const toggles = this.pageEl.querySelectorAll("[data-order-actions-toggle]")
    for (const toggle of toggles) {
      Dropdown.getOrCreateInstance(toggle as HTMLElement)
    }
  }

  private highlightCard = (orderId: string): void => {
    const allCards = this.pageEl.querySelectorAll("[data-order-id]")
    for (const card of allCards) {
      const el = card as HTMLElement
      if (el.dataset.orderAction) continue
      el.classList.toggle(PageOrders.CSS_ACTIVE, el.dataset.orderId === orderId)
    }
  }

  private setupOrderCards = (): void => {
    const cards = this.pageEl.querySelectorAll("[data-order-id]")
    for (const card of cards) {
      const el = card as HTMLElement
      if (el.dataset.orderAction) continue
      el.addEventListener("click", () => this.selectOrder(el.dataset.orderId))
    }
  }

  private selectOrder = async (orderId: string): Promise<void> => {
    this.highlightCard(orderId)
    this.updateUrlParam("orderId", orderId)
    this.pageEl.classList.add(PageOrders.CSS_ORDER_SELECTED)

    const detailBody = this.pageEl.querySelector("[data-orders-detail-body]") as HTMLElement
    if (!detailBody) return

    try {
      const response = await fetch(`${this.baseUrl}/details?id=${encodeURIComponent(orderId)}`)
      if (!response.ok) return
      detailBody.innerHTML = await response.text()
      this.setupActionButtons()
      this.syncSoundAlarmButton()
      this.updateConnectionStatus(SocketClient.isConnected())
    } catch {
      // Network error. Leave existing content
    }
  }

  private setupBackButton = (): void => {
    const backBtn = this.pageEl.querySelector("[data-orders-back]")
    backBtn?.addEventListener("click", () => {
      this.pageEl.classList.remove(PageOrders.CSS_ORDER_SELECTED)
      this.updateUrlParam("orderId", null)
    })
  }

  private setupActionButtons = (): void => {
    const actionButtons = this.pageEl.querySelectorAll("[data-order-action]")
    for (const btn of actionButtons) {
      btn.addEventListener("click", () => {
        const el = btn as HTMLElement
        const action = el.dataset.orderAction
        const orderId = el.dataset.orderId
        if (action === "reject") {
          this.pendingRejectOrderId = orderId
          const isDeliveryType = el.dataset.orderDeliveryType === FoodOrderDeliveryType.Delivery
          this.updateRejectReasonVisibility(isDeliveryType)
          this.rejectModal?.show()
          return
        }
        if (action === "accept") {
          this.pendingAcceptOrderId = orderId
          this.acceptModal?.show()
          return
        }
        if (action === "done") {
          SocketClient.emit(SocketEvents.OrderDone, { orderId })
          return
        }
        if (action === "in-progress") {
          SocketClient.emit(SocketEvents.OrderInProgress, { orderId })
          return
        }
        if (action === "refund") {
          this.pendingRefundOrderId = orderId
          this.refundModal?.show()
          return
        }
      })
    }
    this.setupDropdowns()
  }

  private acceptOrder = (orderId: string, waitingTimeMin: number): void => {
    SocketClient.emit(SocketEvents.OrderAccept, { orderId, waitingTimeMin })
  }

  private rejectOrder = (orderId: string, reason: string): void => {
    SocketClient.emit(SocketEvents.OrderReject, { orderId, reason })
  }

  private refundOrder = (orderId: string): void => {
    SocketClient.emit(SocketEvents.OrderRefund, { orderId })
  }

  private setupSocketListeners = (): void => {
    const onOrderUpdated = async (data: { orderId: string }) => {
      await this.refreshList()

      const currentOrderId = new URLSearchParams(window.location.search).get("orderId")
      if (currentOrderId && currentOrderId === data.orderId) {
        await this.selectOrder(currentOrderId)
      }
    }

    SocketClient.on(SocketEvents.OrderUpdated, onOrderUpdated)
    this.orderUpdatedHandler = onOrderUpdated

    const onOrderNew = async () => {
      const pageParam = new URLSearchParams(window.location.search).get("page")
      const isOnFirstPage = !pageParam || pageParam === "1"
      if (!isOnFirstPage) return
      await this.refreshList()
    }

    SocketClient.on(SocketEvents.OrderNew, onOrderNew)
    this.orderNewHandler = onOrderNew

    const onOrderPending = async () => {
      await this.refreshList()
    }

    SocketClient.on(SocketEvents.OrderPending, onOrderPending)
    this.orderPendingHandler = onOrderPending
  }

  private setupConnectionStatus = (): void => {
    this.updateConnectionStatus(SocketClient.isConnected())

    const onConnectionChange = (connected: boolean) => this.updateConnectionStatus(connected)
    SocketClient.onConnectionChange(onConnectionChange)
    this.connectionChangeHandler = onConnectionChange
  }

  private updateConnectionStatus = (connected: boolean): void => {
    const statusEl = this.pageEl.querySelector("[data-connection-status]") as HTMLElement
    if (!statusEl) return

    statusEl.classList.toggle("connected", connected)
    statusEl.classList.toggle("disconnected", !connected)

    const labelEl = statusEl.querySelector("[data-connection-label]") as HTMLElement
    if (!labelEl) return
    labelEl.textContent = connected ? statusEl.dataset.labelConnected : statusEl.dataset.labelDisconnected
  }

  private fixPaginatorLinks = (): void => {
    for (const link of this.pageEl.querySelectorAll<HTMLAnchorElement>(".pagination a.page-link[href]")) {
      const linkUrl = new URL(link.href, window.location.origin)
      linkUrl.pathname = window.location.pathname
      link.href = `${linkUrl.pathname}${linkUrl.search}`
    }
  }

  private refreshList = async (): Promise<void> => {
    try {
      const response = await fetch(`${this.baseUrl}/list${window.location.search}`)
      if (!response.ok) return
      const listPanel = this.pageEl.querySelector("[data-orders-list-panel]")
      if (!listPanel) return
      listPanel.outerHTML = await response.text()
      this.fixPaginatorLinks()
      this.setupOrderCards()

      const params = new URLSearchParams(window.location.search)
      const currentOrderId = params.get("orderId")
      if (currentOrderId) this.highlightCard(currentOrderId)
    } catch {
      // Network error - leave existing content
    }
  }

  onPageClose = (): void => {
    if (this.orderUpdatedHandler) {
      SocketClient.off(SocketEvents.OrderUpdated, this.orderUpdatedHandler)
    }
    if (this.orderNewHandler) {
      SocketClient.off(SocketEvents.OrderNew, this.orderNewHandler)
    }
    if (this.orderPendingHandler) {
      SocketClient.off(SocketEvents.OrderPending, this.orderPendingHandler)
    }
    if (this.connectionChangeHandler) {
      SocketClient.offConnectionChange(this.connectionChangeHandler)
    }
  }
}
